import { forwardRef, useCallback, useMemo } from "react"

// --- Lib ---
import { parseShortcutKeys } from "@/tiptap/lib/tiptap-utils"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

import {
  COLOR_TEXT_SHORTCUT_KEY,
  useColorText,
} from "@/tiptap/ui/color-text-button"

import { Button } from "@/tiptap/ui-primitive/button"
import { Badge } from "@/tiptap/ui-primitive/badge"

// --- Styles ---

export function ColorTextShortcutBadge({
  shortcutKeys = COLOR_TEXT_SHORTCUT_KEY
}) {
  return <Badge>{parseShortcutKeys({ shortcutKeys })}</Badge>;
}

/**
 * Button component for applying text colors in a Tiptap editor.
 *
 * For custom button implementations, use the `useColorText` hook instead.
 */
export const ColorTextButton = forwardRef((
  {
    editor: providedEditor,
    textColor,
    text,
    hideWhenUnavailable = false,
    onApplied,
    showShortcut = false,
    onClick,
    children,
    style,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const {
    isVisible,
    canColorText,
    isActive,
    handleColorText,
    label,
    shortcutKeys,
    Icon,
  } = useColorText({
    editor,
    textColor,
    label: text || `Color text to ${textColor}`,
    hideWhenUnavailable,
    onApplied,
  })

  const handleClick = useCallback((event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    handleColorText()
  }, [handleColorText, onClick])

  const buttonStyle = useMemo(() =>
    ({
      ...style,
      "--color-text-button-color": textColor
    }), [textColor, style])

  if (!isVisible) {
    return null
  }

  return (
    <Button
      type="button"
      variant="ghost"
      data-active-state={isActive ? "on" : "off"}
      role="button"
      tabIndex={-1}
      disabled={!canColorText}
      data-disabled={!canColorText}
      aria-label={label}
      aria-pressed={isActive}
      tooltip={label}
      onClick={handleClick}
      style={buttonStyle}
      {...buttonProps}
      ref={ref}>
      {children ?? (
        <>
          <span className="tiptap-button-color-text" style={{ color: textColor }}>
            <Icon className="tiptap-button-icon" style={{ color: textColor, flexGrow: 1 }} />
          </span>
          {text && <span className="tiptap-button-text">{text}</span>}
          {showShortcut && (
            <ColorTextShortcutBadge shortcutKeys={shortcutKeys} />
          )}
        </>
      )}
    </Button>
  );
})

ColorTextButton.displayName = "ColorTextButton"
